import { AccountService, TrackingService } from '@sm360/widget-commons-general';
import { LeadApiFp } from '@sm360/lead-submission-fetch-clientapi';
import { ConfigsService, TokenService, WindowProxy } from '@sm360/widget-commons-core-services';
import VEHICLE_TYPES from '../constants/VehicleTypes.js';
import LEAD_TYPES from '../constants/LeadTypes.js';
import envConfig from '../config';

// temporary forced valdation for facebook account creation
//------------------
// function isLeadSubmissionRequestValid(leadSubmissionRequest) {
//     const { contact } = leadSubmissionRequest.lead;
//     const contactHasMinimumInfo = contact.firstname && contact.lastname && (contact.email1 || contact.homePhone);
//     return contactHasMinimumInfo;
// }
//------------------

class LeadSubmissionBuilder {
    /**
     * Service to build and submit a lead
     * @example
     * new LeadSubmissionBuilder()
     *      .addContactInformation('Rick', 'Sanchez', '5141231234', 'valid_email')
     *      .addLeadType(LEAD_TYPES.ACCOUNT_CREATION)
     *      .addQuote(VALID_QUOTE)
     *      .addVehicle(VALID_QUOTE)
     *      .submit()
     */
    constructor(config, formName, standAlone) {
        const date = new Date();
        const unixTimeStamp = Math.floor(date.getTime() / 1000);
        this.leadSubmissionRequest = {
            lead: {
                organizationId: config?.orgId || ConfigsService.getOrganizationId(),
                organizationUnitId: config?.orgUnitId || ConfigsService.getOrganizationUnitId(),
                sourceTypeId: 0, // 0 = Web
                contact: {},
                tracking: {
                    type: 'BASIC',
                    websiteId: config?.websiteId || ConfigsService.getWebsiteId(),
                    url: WindowProxy.get() ? WindowProxy.location().href : undefined,
                    trackingPaths: [
                        {
                            path: WindowProxy.get() ? WindowProxy.location().href : undefined,
                            pathDate: unixTimeStamp,
                        },
                    ],
                    formName, // TODO: Need to change with actual form name
                    date: unixTimeStamp,
                },
                vehicle: {},
                media: 'Showroom',
            },
            subscriptionInfo: {
                contexts: [],
                info: 'Showroom Sign Up Form',
            },
            standAlone,
        };
    }

    /**
     * Setting up the contact information
     * @param {string} firstname
     * @param {string} lastname
     * @param {string} phone
     * @param {string} email
     * @param {number} [contactId]
     */
    addContactInformation(firstname, lastname, phone, email, contactId, mobilePhone) {
        this.leadSubmissionRequest.lead.contact.firstname = firstname;
        this.leadSubmissionRequest.lead.contact.lastname = lastname;
        this.leadSubmissionRequest.lead.contact.homePhone = phone;
        this.leadSubmissionRequest.lead.contact.mobilePhone = mobilePhone;
        this.leadSubmissionRequest.lead.contact.email1 = email;
        this.leadSubmissionRequest.lead.contact.id = contactId;
        return this;
    }

    /**
     * Setting up account creation information
     */
    setIsAccountCreation() {
        this.leadSubmissionRequest.accountCreation = true;
        return this;
    }

    /**
     * Setting up the lead type ID
     * @param {LEAD_TYPE} leadType
     */
    addLeadType(leadType) {
        if (!Object.values(LEAD_TYPES).includes(leadType)) {
            throw new Error(`Lead type [${leadType}] is not supported`);
        }

        this.leadSubmissionRequest.lead.typeId = leadType;
        return this;
    }

    /**
     * Setting up opt-in email
     * @param {boolean} optInEmail
     */
    addOptInEmail(optInEmail) {
        if (optInEmail) {
            this.leadSubmissionRequest.subscriptionInfo.contexts.push('newsletter', 'marketing', 'service');
        }

        return this;
    }

    /**
     * Setting up quote information
     * @param {object} quote.id
     */
    addQuote(quote) {
        if (quote) {
            this.leadSubmissionRequest.quoteId = quote.id;
        }

        return this;
    }

    /**
     * Setting up vehicle information
     * @param {object} quote
     */
    addVehicle(quote) {
        if (quote && quote.vehicle) {
            const { vehicle, vehicleId } = quote;
            const vehicleInventoryId = vehicleId || vehicle.vehicleId;
            this.leadSubmissionRequest.lead.vehicle.vehicleInventoryId = vehicleInventoryId;
            this.leadSubmissionRequest.lead.vehicle.catalogVehicleId = vehicle.catalogVehicleId;
            this.leadSubmissionRequest.lead.vehicle.typeId = vehicleInventoryId
                ? VEHICLE_TYPES.INVENTORY
                : VEHICLE_TYPES.CATALOG;
        }

        return this;
    }

    /**
     * Setting up vehicle information without quote
     * @param {number} vehicleInventoryId
     * @param {number} catalogVehicleId
     */
    addVehicleWithoutQuote(vehicleInventoryId = 0, catalogVehicleId = 0) {
        if (!vehicleInventoryId && !catalogVehicleId) return this;

        const isInventory = Boolean(vehicleInventoryId);
        const result = {
            ...(isInventory ? { vehicleInventoryId } : { catalogVehicleId }),
            typeId: isInventory ? VEHICLE_TYPES.INVENTORY : VEHICLE_TYPES.CATALOG,
        };

        return Object.assign(this.leadSubmissionRequest.lead.vehicle, result);
    }

    /**
     * Setting up the trade in vehicle details
     * @param {number} year
     * @param {string} make
     * @param {string} model
     * @param {string} trim
     * @param {string} bodyStyle
     * @param {string} color
     * @param {number} kilometers
     * @param {string} details
     * @param {string} vin
     */
    addTradeInVehicle({
        year,
        make,
        model,
        trim,
        bodyStyle,
        exteriorColor,
        interiorColor,
        kilometers,
        details = '',
        vin = '',
    }) {
        const tradeIn = {
            year,
            make,
            model,
            trim,
            bodyStyle,
            exteriorColor,
            interiorColor,
            kilometers,
            details,
            vin,
            accountId: AccountService.getUserAccountId() || 0,
        };

        this.leadSubmissionRequest.lead.tradeIn = tradeIn;

        return this;
    }

    /**
     * Setting up the estimation's details
     * @param {number} value
     * @param {number} low
     * @param {number} high
     */
    addTradeInEstimation(value, low, high, valueWasUpdated) {
        if (!this.leadSubmissionRequest.lead.tradeIn) {
            this.leadSubmissionRequest.lead.tradeIn = {};
        }
        this.leadSubmissionRequest.lead.tradeIn.estimation = {};
        this.leadSubmissionRequest.lead.tradeIn.estimation.amount = value;
        this.leadSubmissionRequest.lead.tradeIn.estimation.high = high;
        this.leadSubmissionRequest.lead.tradeIn.estimation.low = low;
        this.leadSubmissionRequest.lead.tradeIn.estimation.amountUpdated = valueWasUpdated;
        return this;
    }

    /**
     * Setting up the trade in questionnaire
     * @param {object} evaluation
     * @param {*} evaluation.questionKey Key represents the question and the value is the answer
     */
    addTradeInEvaluation(evaluation) {
        if (!this.leadSubmissionRequest.lead.tradeIn) {
            this.leadSubmissionRequest.lead.tradeIn = {};
        }
        this.leadSubmissionRequest.lead.tradeIn.conditionForm = evaluation;
        return this;
    }

    addFinancingInformation() {
        // TODO when migrating financing
        throw new Error('Not yet implemented');
    }

    // Temporary added the extra info in basic info texbox
    // Change it with activity once confirm with CRM
    addBasicInfo(info) {
        this.leadSubmissionRequest.lead.info = info;
        return this;
    }

    deleteVehicle() {
        delete this.leadSubmissionRequest.lead.vehicle;
    }

    submit(skipContactId) {
        const accountPromise = AccountService.isAuthenticated()
            ? AccountService.getLoggedAccount()
            : Promise.resolve({});

        return accountPromise.then(({ contactId }) => {
            if (!this.leadSubmissionRequest.lead.contact.id && !skipContactId) {
                this.leadSubmissionRequest.lead.contact.id = contactId;
            }
            return TrackingService.getSessionTrackingPath().then((trackingPaths) => {
                if (trackingPaths && trackingPaths.length > 0) {
                    this.leadSubmissionRequest.lead.tracking.trackingPaths = trackingPaths;
                }
                // temporary forced valdation for facebook account creation
                // ------------------
                // if (!isLeadSubmissionRequestValid(this.leadSubmissionRequest)) {
                //     return Promise.reject(new Error('Lead is invalid. Missing contact information'));
                // }
                // -------------------

                return TokenService.fetch().then((accessToken) => {
                    return LeadApiFp({
                        accessToken,
                    }).send(this.leadSubmissionRequest)(undefined, envConfig.endpoint.leadSubmission);
                });
            });
        });
    }
}

export default LeadSubmissionBuilder;
